import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Row } from 'reactstrap';

import IUserGet from 'api/interfaces/users/IUserGet';
import { WRITE_CONTACTS } from 'services/enums/users/permissions';

import customerApi from 'api/contacts';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import LinkButton from 'components/layout/buttons/LinkButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';
import { showDesigners } from 'components/offers/OfferSelectOptions';

import NewProjectContactModal, {
  NEW_COMPANY,
  NEW_PERSON,
} from './NewProjectContactModal';

import 'style/offers/ProjectContacts.css';

// The roles of the contacts of the project besides the customers (backend ContactTypes)
export const PROJECT_CONTACT_ROLES = [
  'designer',
  'constructor',
  'contractor',
  'inspector',
  'other',
];

export interface IProjectContactRow {
  key: number;
  office: { label: string; value: string } | null;
  personId: string;
  // The contact persons of the company, null until loaded
  persons: any[] | null;
  role: string;
}

const loadPersons = async (officeId: string): Promise<any[]> => {
  try {
    const contact = await customerApi.getContact(officeId, {
      ownOrganizationUnit: true,
    });
    return contact.contactPersons ?? [];
  } catch (error) {
    return [];
  }
};

// Builds the rows from the saved contacts of the project (with their roles). The contact
// persons are loaded by the rows themselves, so the rows show at once.
export const getProjectContactRows = (
  contacts: {
    contact: { id: number; companyName: string };
    contactPerson: { id: number } | null;
    type?: string;
  }[]
): IProjectContactRow[] =>
  contacts.map((projectContact, index) => ({
    key: index + 1,
    office: {
      label: projectContact.contact.companyName,
      value: projectContact.contact.id.toString(),
    },
    personId: projectContact.contactPerson?.id?.toString() ?? '',
    persons: null,
    role: PROJECT_CONTACT_ROLES.includes(projectContact.type ?? '')
      ? (projectContact.type as string)
      : 'designer',
  }));

// The rows with a company, as saved with the offer
export const getProjectContactsPost = (
  rows: IProjectContactRow[]
): { contactId: number; contactPersonId?: number; type: string }[] =>
  rows
    .filter((row) => row.office !== null && row.office.value !== '')
    .map((row) => ({
      contactId: parseInt(row.office?.value ?? '', 10),
      contactPersonId:
        row.personId === '' ? undefined : parseInt(row.personId, 10),
      type: row.role,
    }));

/**
 * The contacts of the project besides the customers, e.g. the architects, the main
 * contractor and the inspector: rows of a role, a company and a contact person. The
 * section is opened and closed from its title with the plus and minus icons.
 */
const ProjectContacts = ({
  locked,
  rows,
  searchOffice,
  setRows,
}: {
  locked: boolean;
  rows: IProjectContactRow[];
  searchOffice: (inputValue: string) => Promise<any>;
  setRows: Dispatch<SetStateAction<IProjectContactRow[]>>;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);
  const [isOpen, setIsOpen] = useState(true);
  // A new company or contact person being created on a row
  const [newContact, setNewContact] = useState<null | {
    mode: string;
    rowKey: number;
  }>(null);
  const newContactRow =
    newContact === null
      ? undefined
      : rows.find((row) => row.key === newContact.rowKey);

  // Companies and persons can be created by the users who can edit the contacts
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const canCreate =
    !locked &&
    (currentUser?.permissions?.some(
      (permission) => permission.type === WRITE_CONTACTS
    ) ??
      false);
  // The companies whose contact persons are being loaded, by row: "key:company id"
  const loading = useRef(new Set<string>());

  // The contact persons of each company are loaded in the background. The rows are always
  // updated from their latest state, and only while the row still has the same company, so
  // a slow answer never overwrites what the user has changed meanwhile.
  useEffect(() => {
    rows
      .filter((row) => row.persons === null && row.office?.value)
      .forEach((row) => {
        const officeId = row.office?.value ?? '';
        const loadKey = `${row.key}:${officeId}`;

        if (loading.current.has(loadKey)) {
          return;
        }

        loading.current.add(loadKey);
        loadPersons(officeId).then((persons) => {
          loading.current.delete(loadKey);
          setRows((current) =>
            current.map((currentRow) =>
              currentRow.key === row.key &&
              currentRow.office?.value === officeId
                ? { ...currentRow, persons }
                : currentRow
            )
          );
        });
      });
  }, [rows, setRows]);

  const updateRow = (key: number, changes: Partial<IProjectContactRow>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...changes } : row))
    );

  const changeOffice = (
    key: number,
    office: { label: string; value: string } | null
  ) =>
    updateRow(key, {
      office,
      personId: '',
      persons: office !== null && office.value !== '' ? null : [],
    });

  const addRow = () => {
    setRows((current) => [
      ...current,
      {
        key: Math.max(0, ...current.map((row) => row.key)) + 1,
        office: null,
        personId: '',
        persons: [],
        role: current.length === 0 ? 'designer' : 'other',
      },
    ]);
    setIsOpen(true);
  };

  const removeRow = (key: number) =>
    setRows((current) => current.filter((row) => row.key !== key));

  // A company or a person created on a row is chosen on it
  const contactCreated = (created: { id: number; label: string }) => {
    if (newContact === null) {
      return;
    }

    if (newContact.mode === NEW_COMPANY) {
      changeOffice(newContact.rowKey, {
        label: created.label,
        value: created.id.toString(),
      });
    } else {
      // The persons of the company are loaded again with the new one
      updateRow(newContact.rowKey, {
        personId: created.id.toString(),
        persons: null,
      });
    }

    setNewContact(null);
  };

  const toggle = () => setIsOpen(!isOpen);

  return (
    <div className="project-contacts">
      <div
        className="project-contacts-title"
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <img
          src={isOpen ? minimize : maximize}
          alt={t(`${translations.offers}:projectContacts.Title`)}
          className="collapse-icon"
        />
        {t(`${translations.offers}:projectContacts.Title`)}
        {isOpen || rows.length === 0 ? null : (
          <span className="project-contacts-count">({rows.length})</span>
        )}
      </div>
      {isOpen ? (
        <>
          {rows.length === 0 ? (
            <p className="project-contacts-info">
              {t(`${translations.offers}:projectContacts.Empty`)}
            </p>
          ) : (
            <Row className="project-contacts-header">
              <Col md={3}>
                {t(`${translations.offers}:projectContacts.Role`)}
              </Col>
              <Col md={4}>
                {t(`${translations.offers}:projectContacts.Company`)}
              </Col>
              <Col md={4}>
                {t(`${translations.offers}:projectContacts.Person`)}
              </Col>
            </Row>
          )}
          {rows.map((row) => (
            <Row key={row.key}>
              <Col md={3}>
                <SelectInputFieldControlled
                  value={row.role}
                  id={`project-contact-role-${row.key}`}
                  name={`project-contact-role-${row.key}`}
                  onChange={(event: any) =>
                    updateRow(row.key, { role: event.target.value })
                  }
                  options={PROJECT_CONTACT_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(
                        `${translations.offers}:projectContacts.roles.${role}`
                      )}
                    </option>
                  ))}
                  disabled={locked}
                />
              </Col>
              <Col md={4}>
                <FormGroup>
                  <SearchDropdown
                    value={row.office}
                    placeholder={t(`${translations.common}:Search`)}
                    className="text-field search-field"
                    name={`project-contact-office-${row.key}`}
                    onChange={(office: any) => changeOffice(row.key, office)}
                    loadOptions={searchOffice}
                    disabled={locked}
                  />
                  {canCreate ? (
                    <LinkButton
                      className="project-contacts-new"
                      onClick={() =>
                        setNewContact({ mode: NEW_COMPANY, rowKey: row.key })
                      }
                    >
                      {`+ ${t(
                        `${translations.offers}:projectContacts.New company`
                      )}`}
                    </LinkButton>
                  ) : null}
                </FormGroup>
              </Col>
              <Col md={4}>
                <SelectInputFieldControlled
                  value={row.personId}
                  id={`project-contact-person-${row.key}`}
                  name={`project-contact-person-${row.key}`}
                  onChange={(event: any) =>
                    updateRow(row.key, { personId: event.target.value })
                  }
                  options={showDesigners(row.persons ?? [])}
                  disabled={locked || row.office === null}
                />
                {canCreate && row.office !== null && row.office.value !== '' ? (
                  <LinkButton
                    className="project-contacts-new project-contacts-new-person"
                    onClick={() =>
                      setNewContact({ mode: NEW_PERSON, rowKey: row.key })
                    }
                  >
                    {`+ ${t(
                      `${translations.offers}:projectContacts.New person`
                    )}`}
                  </LinkButton>
                ) : null}
              </Col>
              <Col md={1}>
                {locked ? null : (
                  <TrashIcon
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => removeRow(row.key)}
                  />
                )}
              </Col>
            </Row>
          ))}
          {locked ? null : (
            <LinkButton onClick={addRow}>
              {`+ ${t(`${translations.offers}:projectContacts.Add`)}`}
            </LinkButton>
          )}
        </>
      ) : null}
      <NewProjectContactModal
        company={newContactRow?.office ?? null}
        mode={newContact?.mode ?? null}
        onCreated={contactCreated}
        organizationUnitId={currentUser?.organizationUnit?.id}
        role={newContactRow?.role ?? 'other'}
        toggle={() => setNewContact(null)}
      />
    </div>
  );
};

export default ProjectContacts;
